import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Skeleton } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import EmptyState from "global/components/EmptyState/EmptyState";
import SettingsLayout from "../components/SettingsLayout";
import SettingsSearchField from "../components/SettingsSearchField";
import { useTitle } from "utils/UseTitle";
import strings from "global/constants/StringConstants";
import { useAppSelector } from "redux/hooks";
import { selectFeaturesLoaded } from "redux/featuresSlice";
import { debounceEventHandler, handleErrorMessage } from "helpers/methods";
import {
  Workspace,
  WorkspaceEnrollment as Enrollment,
  WorkspaceMembership,
  WorkspaceRole,
} from "types/workspace";
import { fetchWorkspaces, searchEnrollment } from "./WorkspacesServices";
import { fetchWorkspaceMembers } from "./WorkspaceMembersServices";
import { fetchWorkspaceRoles } from "./WorkspaceRolesServices";
import WorkspacesBackLink from "./components/WorkspacesBackLink";
import WorkspaceListSkeleton from "./components/WorkspaceListSkeleton";
import { toTree, WorkspaceNode } from "./workspaceTree";
import workspacesTokens from "./Workspaces.tokens";
import {
  cardSx,
  columnHeaderSx,
  listRowSx,
  nameCellSx,
  pillSx,
  rowTextSx,
  treeNameCellSx,
  treeRowSx,
  treeToggleSpacerSx,
  treeToggleSx,
} from "./Workspaces.styles";

const { pill, list } = workspacesTokens;
const TREE_GRID = "minmax(0, 1fr) 160px";
const RESULT_GRID = "minmax(0, 1fr) minmax(0, 1.4fr) 160px";
const MIN_SEARCH_LENGTH = 2;

/**
 * Enrollment: who belongs to which workspace across the tree. Account admins see every workspace;
 * a delegated admin only the subtree they manage (the backend scopes both the tree and the search).
 * Same shell as the Teams and Roles screens: the search sits in the page header and filters as you
 * type; the card shows the matches, or the workspace tree when nothing is searched.
 */
const WorkspaceEnrollment = () => {
  useTitle("Workspace enrollment");
  const featuresLoaded = useAppSelector(selectFeaturesLoaded);

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [roles, setRoles] = useState<WorkspaceRole[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  // A workspace's members load the first time it is opened, then stay.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [members, setMembers] = useState<Record<string, WorkspaceMembership[] | "loading">>({});

  const [query, setQuery] = useState<string>("");
  const [results, setResults] = useState<Enrollment[] | null>(null);
  const [searching, setSearching] = useState<boolean>(false);
  // Only the latest search may show its answer: an earlier, slower one is dropped.
  const searchSeq = useRef<number>(0);

  useEffect(() => {
    if (!featuresLoaded) return;
    let current = true;
    (async () => {
      try {
        const [listData, roleList] = await Promise.all([
          fetchWorkspaces(false),
          fetchWorkspaceRoles().catch(() => []),
        ]);
        if (!current) return;
        setWorkspaces(Array.isArray(listData) ? listData : []);
        setRoles(Array.isArray(roleList) ? roleList : []);
      } catch (error: any) {
        if (current) handleErrorMessage(error);
      } finally {
        if (current) setLoading(false);
      }
    })();
    return () => {
      current = false;
    };
  }, [featuresLoaded]);

  const roleName = (id: number) => roles.find((r) => r.id === id)?.name ?? `Role ${id}`;

  const toggle = async (ws: Workspace) => {
    const next = new Set(open);
    if (next.has(ws.id)) {
      next.delete(ws.id);
      setOpen(next);
      return;
    }
    next.add(ws.id);
    setOpen(next);
    if (members[ws.id]) return;
    setMembers((prev) => ({ ...prev, [ws.id]: "loading" }));
    try {
      const memberList = await fetchWorkspaceMembers(ws.id);
      setMembers((prev) => ({ ...prev, [ws.id]: Array.isArray(memberList) ? memberList : [] }));
    } catch (error: any) {
      setMembers((prev) => {
        const copy = { ...prev };
        delete copy[ws.id];
        return copy;
      });
      handleErrorMessage(error);
    }
  };

  const runSearch = async (raw: string) => {
    const text = raw.trim();
    const seq = ++searchSeq.current;
    setQuery(text);
    if (text.length < MIN_SEARCH_LENGTH) {
      setResults(null);
      setSearching(false);
      return;
    }
    try {
      setSearching(true);
      const rows = await searchEnrollment(text);
      if (seq === searchSeq.current) setResults(Array.isArray(rows) ? rows : []);
    } catch (error: any) {
      if (seq === searchSeq.current) handleErrorMessage(error);
    } finally {
      if (seq === searchSeq.current) setSearching(false);
    }
  };

  // Debounced like the Teams / Roles search; created once so the timer survives re-renders.
  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;
  const onSearchChange = useMemo(
    () =>
      debounceEventHandler(
        (e: any) => runSearchRef.current(e.target.value ?? ""),
        strings.SEARCH_TIME_OUT,
      ),
    [],
  );

  /** An indented row inside an opened workspace: spacer where the toggle would be, then text. */
  const memberRow = (key: string, level: number, cells: ReactNode[]) => (
    <Box key={key} sx={treeRowSx(TREE_GRID, false, level + 1)}>
      <Box sx={treeNameCellSx(level + 1)}>
        <Box component="span" sx={treeToggleSpacerSx} />
        {cells[0]}
      </Box>
      {cells[1] ?? <Box component="span" />}
    </Box>
  );

  const renderMembers = (ws: Workspace, level: number) => {
    const memberList = members[ws.id];
    if (memberList === "loading" || memberList === undefined) {
      return memberRow(`${ws.id}-loading`, level, [
        <Skeleton
          key="name"
          variant="text"
          width="40%"
          sx={{ fontSize: list.font.rowText }}
          aria-label="Loading members"
        />,
        <Skeleton key="role" variant="text" width="60%" sx={{ fontSize: list.font.rowText }} />,
      ]);
    }
    if (memberList.length === 0) {
      return memberRow(`${ws.id}-empty`, level, [
        <Box key="empty" component="span" sx={rowTextSx("body", "regular")}>
          No members in this workspace.
        </Box>,
      ]);
    }
    return memberList.map((m) =>
      memberRow(m.id, level, [
        <Box key="email" component="span" sx={rowTextSx("body", "regular")}>
          {m.userEmail}
        </Box>,
        <Box key="role" component="span" sx={rowTextSx("body", "regular")}>
          {roleName(m.roleId)}
        </Box>,
      ]),
    );
  };

  const renderNode = (node: WorkspaceNode, isLast: boolean) => {
    const { workspace: ws, level } = node;
    const count = ws.memberCount ?? 0;
    const expanded = open.has(ws.id);
    return (
      <Box key={ws.id}>
        <Box sx={treeRowSx(TREE_GRID, isLast && !expanded, level)}>
          <Box sx={treeNameCellSx(level)}>
            {count > 0 ? (
              <IconButton
                size="small"
                aria-label={`${expanded ? "Hide" : "Show"} the members of ${ws.name}`}
                aria-expanded={expanded}
                onClick={() => toggle(ws)}
                sx={treeToggleSx}
              >
                {expanded ? (
                  <ExpandMoreRoundedIcon fontSize="small" />
                ) : (
                  <ChevronRightRoundedIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <Box component="span" sx={treeToggleSpacerSx} />
            )}
            <Box sx={nameCellSx}>
              <Box component="span" sx={rowTextSx("ink", "strong")}>
                {ws.name}
              </Box>
              {ws.isDefault && (
                <Box component="span" sx={pillSx(pill.neutral)}>
                  Default
                </Box>
              )}
            </Box>
          </Box>
          <Box component="span" sx={rowTextSx("body", "regular")}>
            {`${count} member${count === 1 ? "" : "s"}`}
          </Box>
        </Box>
        {expanded && renderMembers(ws, level)}
      </Box>
    );
  };

  const renderTree = () => {
    if (loading) {
      return <WorkspaceListSkeleton columns={TREE_GRID} middleCells={["40%"]} showAction={false} />;
    }
    if (workspaces.length === 0) {
      return (
        <EmptyState icon="folder" title="No workspaces" body="There are no active workspaces to show." />
      );
    }
    const nodes = toTree(workspaces);
    return nodes.map((node, index) => renderNode(node, index === nodes.length - 1));
  };

  const renderResults = () => {
    if (searching) {
      return (
        <WorkspaceListSkeleton columns={RESULT_GRID} middleCells={["55%", "50%"]} showAction={false} />
      );
    }
    if (!results || results.length === 0) {
      return (
        <EmptyState
          icon="search"
          title="No one found"
          body={`No member of these workspaces has an email containing "${query}".`}
        />
      );
    }
    return results.map((r, index) => (
      <Box key={r.membershipId} sx={listRowSx(RESULT_GRID, index === results.length - 1)}>
        <Box component="span" sx={rowTextSx("ink", "strong")}>
          {r.userEmail}
        </Box>
        <Box sx={nameCellSx}>
          <Box component="span" sx={rowTextSx("body", "regular")}>
            {r.path.join(" › ")}
          </Box>
          {r.archived && (
            <Box component="span" sx={pillSx(pill.warning)}>
              Archived
            </Box>
          )}
        </Box>
        <Box component="span" sx={rowTextSx("body", "regular")}>
          {r.roleName ?? roleName(r.roleId)}
        </Box>
      </Box>
    ));
  };

  const showingResults = query.length >= MIN_SEARCH_LENGTH;

  return (
    <SettingsLayout
      activeTab="Workspaces"
      title="Workspace enrollment"
      headerAction={
        <SettingsSearchField
          id="workspace_enrollment_search_box"
          placeholder="Search people by email"
          onChange={onSearchChange}
        />
      }
    >
      <WorkspacesBackLink />
      <Box sx={cardSx}>
        {showingResults ? (
          <>
            <Box sx={columnHeaderSx(RESULT_GRID)}>
              <Box component="span">Email</Box>
              <Box component="span">Workspace</Box>
              <Box component="span">Role</Box>
            </Box>
            <Box aria-live="polite" aria-busy={searching}>
              {renderResults()}
            </Box>
          </>
        ) : (
          <>
            <Box sx={columnHeaderSx(TREE_GRID)}>
              <Box component="span">Workspace</Box>
              <Box component="span">Members</Box>
            </Box>
            <Box aria-busy={loading}>{renderTree()}</Box>
          </>
        )}
      </Box>
    </SettingsLayout>
  );
};

export default WorkspaceEnrollment;
